import styled from "styled-components";
import { BlurModal } from "../BlurModal/BlurModal";
import { GeneralModal } from "../../../components/GeneralModal/GeneralModal";
import { LabelForRadioButton } from "../Input/SwitchButton";
import { useState } from "react";
import { baseURL } from "../../../../../constant";
import { format } from "date-fns";
import { downloadUrls, fileNameForDownload } from "./constant";
import { useNotification } from "../../../../../hooks/useNotification";
import { useLazyDownloadExportExcelFileQuery } from "../../../../../store/apiSlice/newInterfaceApiSlice";
import { allocationDataByType } from "../../Allocation/SelectAllocation";

/**
 * ExportData Component
 *
 * A modal dialog that allows users to export data (Excel format) either for:
 * - all rows in the current view
 * - or only the currently selected rows.
 *
 * 🔹 It supports all modules.
 * 🔹 It uses Axios to trigger file downloads with proper authentication.
 * 🔹 If rows are selected, defaults to "selected rows" mode.
 * 🔹 Displays errors using a global notification hook.
 *
 * @component
 * @param {Function} closeExportData - Function to close the export modal.
 * @param {Array} selectedRowsFromList - List of selected row IDs.
 * @param {string} orderType - Type of data being exported (e.g., "shipment", "allocation").
 * @param {string} [allocation] - Optional allocation type ("purchase" or "transfers").
 * @param {Function} setSelectedOrder - Function to clear selected rows after export.
 * @param {string | number} idOfCurrentView - ID of the current view (used for all-rows export).
 *
 */
const ExportData = ({
  closeExportData = () => {},
  selectedRowsFromList = [],
  orderType,
  allocation,
  setSelectedOrder,
  idOfCurrentView,
}) => {
  const [selected, setSelected] = useState(
    selectedRowsFromList?.length > 0 ? "selectedRows" : "allRows",
  );
  const [downloadExcelFile] = useLazyDownloadExportExcelFileQuery();
  const notify = useNotification();

  const handleChangeRows = e => setSelected(e.target.value);

  const exportExcel = async () => {
    let setOrderType = orderType;
    if (allocation) {
      setOrderType = allocationDataByType[allocation].exportConstant;
    }
    let fileUrl = baseURL + downloadUrls[setOrderType];
    const url = new URL(fileUrl + "export_excel/");

    if (selected === "selectedRows") {
      const idsForUrl = selectedRowsFromList.join(",");
      url.searchParams.set("ids", idsForUrl);
    } else if (selected === "allRows" && idOfCurrentView) {
      url.searchParams.set("view_id", idOfCurrentView);
    }
    fileUrl = url.toString();

    try {
      const data = await downloadExcelFile(fileUrl).unwrap();

      // Create a temporary URL and trigger file download
      if (data) {
        const fileName =
          fileNameForDownload[setOrderType] + format(new Date(), "yyyyMMdd_HHmmss") + ".xlsx";
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url); //  prevent memory leak
      }

      setSelectedOrder([]);
    } catch (error) {
      let message = "Something went wrong";
      if (error.status === 404) {
        message = "No data available to export.";
      }
      notify({ type: "error", message });
    }
    closeExportData();
  };
  return (
    <BlurModal>
      <GeneralModal
        titleOfModal="Export Data"
        btnName="Export"
        isCancelButttonShow={true}
        closeModal={closeExportData}
        widthOfGeneralModal={widthOfExportModal}
        handleModalWork={exportExcel}
      >
        <ExportDataCss>
          <LabelForRadioButton
            className={selectedRowsFromList?.length === 0 ? "selectedRows" : null}
            checked={selected === "selectedRows" ? true : false}
          >
            <input
              value="selectedRows"
              name="rows"
              type="radio"
              onChange={handleChangeRows}
              checked={selected === "selectedRows"}
              disabled={selectedRowsFromList?.length === 0}
            />
            <div className="radioBtn"></div>
            Export currently selected rows
          </LabelForRadioButton>

          <LabelForRadioButton checked={selected === "allRows" ? true : false}>
            <input
              value="allRows"
              name="rows"
              type="radio"
              onChange={handleChangeRows}
              checked={selected === "allRows"}
            />
            <div className="radioBtn"></div>
            Export all rows
          </LabelForRadioButton>
        </ExportDataCss>
      </GeneralModal>
    </BlurModal>
  );
};
export { ExportData };

const widthOfExportModal = "30vw";

const ExportDataCss = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  > label {
    display: flex;
    gap: 12px;
    color: var(--DARK-GREY, #0e0e2c);
    line-height: 22px;
  }
  > .selectedRows {
    color: var(--character-disabled-placeholder-25, rgba(0, 0, 0, 0.25));
  }
`;
